import React, { useState, useCallback, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';

interface ProductGalleryProps {
  images: string[];
  productName: string;
}

const ProductGallery: React.FC<ProductGalleryProps> = ({ images, productName }) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 50, y: 50 });
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const mainImageRef = useRef<HTMLDivElement>(null);

  const validImages = images.filter(url => url && url.trim() !== '');

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!isZoomed || !mainImageRef.current) return;
    const rect = mainImageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomPosition({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
  }, [isZoomed]);

  const handleMouseEnter = () => {
    if (window.innerWidth >= 768) setIsZoomed(true);
  };
  const handleMouseLeave = () => setIsZoomed(false);

  // Swipe handlers for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
    setTouchEnd(0);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const diff = touchStart - touchEnd;
    const threshold = 50;
    if (diff > threshold && selectedImage < validImages.length - 1) {
      setSelectedImage(prev => prev + 1);
    } else if (diff < -threshold && selectedImage > 0) {
      setSelectedImage(prev => prev - 1);
    }
  };

  const goToPrev = () => setSelectedImage(prev => prev > 0 ? prev - 1 : validImages.length - 1);
  const goToNext = () => setSelectedImage(prev => prev < validImages.length - 1 ? prev + 1 : 0);

  // Keyboard navigation (stable handlers — avoids stale closure on selected index)
  useEffect(() => {
    if (validImages.length === 0) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        setSelectedImage((prev) => (prev > 0 ? prev - 1 : validImages.length - 1));
      }
      if (e.key === 'ArrowRight') {
        setSelectedImage((prev) => (prev < validImages.length - 1 ? prev + 1 : 0));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [validImages.length]);

  if (validImages.length === 0) {
    return (
      <div className="w-full aspect-[3/4] sm:aspect-square md:aspect-[4/5] bg-gray-100 rounded-xl flex items-center justify-center">
        <span className="text-gray-400 text-sm">No images available</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 sm:gap-4 md:gap-5 lg:sticky lg:top-24 lg:self-start w-full min-w-0">
      <div className="flex flex-col md:flex-row gap-2 md:gap-4 md:items-start overflow-hidden w-full min-w-0">

        {/* Desktop: Vertical Scrollable Thumbnails */}
        {validImages.length > 1 && (
          <div className="hidden md:flex flex-col w-14 lg:w-16 flex-shrink-0 overflow-hidden">
            <button
              onClick={() => {
                const el = document.getElementById('desktop-thumbnails');
                if (el) el.scrollBy({ top: -100, behavior: 'smooth' });
              }}
              className="w-full h-7 flex items-center justify-center bg-gradient-to-b from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 rounded-t-lg mb-1 flex-shrink-0 transition-all shadow-sm"
              title="Scroll up"
            >
              <ChevronLeft className="w-3.5 h-3.5 text-gray-600 -rotate-90" />
            </button>

            <div
              id="desktop-thumbnails"
              className="flex flex-col gap-1.5 max-h-[350px] lg:max-h-[450px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100 py-1 overflow-x-hidden pr-1"
              style={{ scrollbarWidth: 'thin' }}
            >
              {validImages.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`w-12 h-12 lg:w-14 lg:h-14 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0 shadow-md ${
                    selectedImage === index
                      ? 'border-blue-500 ring-2 ring-blue-200 shadow-md shadow-blue-500/20'
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  }`}
                >
                  <img
                    src={image}
                    alt={`${productName} ${index + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.src = '/placeholder-product.jpg'; }}
                  />
                </button>
              ))}
            </div>

            <button
              onClick={() => {
                const el = document.getElementById('desktop-thumbnails');
                if (el) el.scrollBy({ top: 100, behavior: 'smooth' });
              }}
              className="w-full h-7 flex items-center justify-center bg-gradient-to-b from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 rounded-b-lg mt-1 flex-shrink-0 transition-all shadow-sm"
              title="Scroll down"
            >
              <ChevronLeft className="w-3.5 h-3.5 text-gray-600 rotate-90" />
            </button>
          </div>
        )}

        {/* Main Image Area */}
        <div className="flex-1 bg-white rounded-2xl overflow-hidden border border-slate-200/90 shadow-[0_1px_3px_rgba(15,23,42,0.06),0_8px_24px_rgba(15,23,42,0.06)] min-w-0 w-full ring-1 ring-slate-900/[0.04]">
          <div
            ref={mainImageRef}
            className="relative w-full aspect-[4/3] sm:aspect-[4/3] md:aspect-square flex items-center justify-center bg-slate-50/80 cursor-crosshair group overflow-hidden"
            onMouseMove={handleMouseMove}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Skeleton loader */}
            {isLoading && (
              <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 animate-pulse z-10" />
            )}

            <img
              src={validImages[selectedImage] || '/placeholder-product.jpg'}
              alt={productName}
              className={`w-full h-full object-contain transition-transform duration-500 ease-out ${
                isZoomed ? 'scale-[2.5]' : 'scale-100'
              }`}
              style={isZoomed ? {
                transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%`
              } : {}}
              onLoad={() => setIsLoading(false)}
              onError={(e) => { e.currentTarget.src = '/placeholder-product.jpg'; setIsLoading(false); }}
            />

            {/* Zoom Hint - Desktop only */}
            <div className="hidden md:block absolute top-3 right-3 bg-white/95 backdrop-blur-md rounded-xl p-2.5 shadow-lg border border-gray-100 opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none">
              <ZoomIn className="w-4 h-4 text-gray-700" />
            </div>

            {/* Navigation Arrows - Always visible on mobile, hover on desktop */}
            {validImages.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); goToPrev(); }}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-9 sm:h-9 bg-white/95 backdrop-blur-md rounded-lg shadow-lg flex items-center justify-center hover:bg-white hover:scale-110 transition-all duration-300 active:scale-95 md:opacity-0 md:group-hover:opacity-100 border border-gray-100"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="w-5 h-5 sm:w-4 sm:h-4 text-gray-700" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); goToNext(); }}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-9 sm:h-9 bg-white/95 backdrop-blur-md rounded-lg shadow-lg flex items-center justify-center hover:bg-white hover:scale-110 transition-all duration-300 active:scale-95 md:opacity-0 md:group-hover:opacity-100 border border-gray-100"
                  aria-label="Next image"
                >
                  <ChevronRight className="w-5 h-5 sm:w-4 sm:h-4 text-gray-700" />
                </button>
              </>
            )}

            {/* Image Counter */}
            {validImages.length > 1 && (
              <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-black/80 to-black/70 backdrop-blur-md text-white text-xs sm:text-xs px-3 py-1.5 rounded-lg pointer-events-none font-semibold shadow-md border border-white/10">
                {selectedImage + 1} / {validImages.length}
              </div>
            )}
          </div>
        </div>

        {/* Mobile: Horizontal Scrollable Thumbnails */}
        {validImages.length > 1 && (
          <div className="flex md:hidden flex-col mt-2">
            <div
              id="mobile-thumbnails"
              className="flex gap-2 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100 pb-2 px-1"
              style={{ scrollbarWidth: 'thin', WebkitOverflowScrolling: 'touch' }}
            >
              {validImages.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-all shadow-md ${
                    selectedImage === index ? 'border-blue-500 ring-2 ring-blue-200 shadow-md shadow-blue-500/20' : 'border-gray-200'
                  }`}
                >
                  <img
                    src={image}
                    alt={`${productName} ${index + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.src = '/placeholder-product.jpg'; }}
                  />
                </button>
              ))}
            </div>

            {/* Mobile Dot Indicators */}
            <div className="flex justify-center mt-2 gap-1.5">
              {validImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    selectedImage === index ? 'bg-blue-500 w-5 shadow-sm shadow-blue-500/30' : 'bg-gray-300 w-2'
                  }`}
                  aria-label={`Go to image ${index + 1}`}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductGallery;
